// src/components/views/images360/scanIndex.js
/**
 * AVA-597: builds a flat, read-only index over every 360 scan of a facility.
 * Pure (no store, no THREE) so it can be unit tested. The store builds it once per data change
 * (src/store/plugins/scanIndexPlugin.js) and keeps it deep-frozen so Vue never observes it.
 */

export const UNASSIGNED_LEVEL = 'unassigned';
const MIN_URL_SEGMENT_LENGTH = 9;
const GUID_PATTERN = '[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}';
const GUID_IN_TEXT = new RegExp(GUID_PATTERN, 'gi');
const GUID_EXACT = new RegExp(`^${GUID_PATTERN}$`, 'i');

const isGuid = (value) => GUID_EXACT.test(String(value));

/** Same test and order as the legacy findLevelForImage: the first level whose box holds the position wins. */
export function findLevelIdForPosition(levels, position) {
  if (!position || !Array.isArray(levels)) return UNASSIGNED_LEVEL;
  for (const level of levels) {
    if (
      position.x >= level.west && position.x <= level.east &&
      position.y >= level.south && position.y <= level.north &&
      position.z >= level.bottomElevation && position.z <= level.topElevation
    ) {
      return level.id;
    }
  }
  return UNASSIGNED_LEVEL;
}

function urlSegments(url) {
  const segments = [];
  for (const raw of String(url).split(/[/\\?&=]/)) {
    if (raw.length < MIN_URL_SEGMENT_LENGTH) continue;
    segments.push(raw);
    const withoutExtension = raw.replace(/\.[^.]+$/, '');
    if (withoutExtension !== raw && withoutExtension.length >= MIN_URL_SEGMENT_LENGTH) segments.push(withoutExtension);
  }
  return segments;
}

export function buildPointCloudLookup(pointClouds) {
  const byUrlSegment = new Map();
  const byName = new Map();
  const byProjectId = new Map();
  // Separate from byUrlSegment so an exact segment hit always beats a GUID embedded mid-segment.
  const byGuid = new Map();
  for (const pc of pointClouds || []) {
    if (pc.url) {
      urlSegments(pc.url).forEach((segment) => byUrlSegment.set(segment, pc));
      // First wins, like the legacy pointClouds.find(pc => pc.url.includes(image.id)).
      (String(pc.url).match(GUID_IN_TEXT) || []).forEach((guid) => {
        const key = guid.toLowerCase();
        if (!byGuid.has(key)) byGuid.set(key, pc);
      });
    }
    if (pc.name && !byName.has(pc.name)) byName.set(pc.name, pc);
    if (pc.projectId && !byProjectId.has(pc.projectId)) byProjectId.set(pc.projectId, pc);
  }
  return { byUrlSegment, byGuid, byName, byProjectId, all: pointClouds || [] };
}

function findPointCloudBySegment(lookup, image) {
  const exact = lookup.byUrlSegment.get(image.id);
  if (exact) return exact;
  return isGuid(image.id) ? lookup.byUrlSegment.get(String(image.id).toLowerCase()) || null : null;
}

/**
 * Legacy images360Tab fallback for a scan with an output URL whose id is embedded in a PC url, not a whole segment.
 * GUID ids use the byGuid index; other ids keep the legacy linear includes().
 */
function findPointCloudByEmbeddedId(lookup, image) {
  if (!image.scanOutputUrl || !image.id) return null;
  if (isGuid(image.id)) return (lookup.byGuid && lookup.byGuid.get(String(image.id).toLowerCase())) || null;
  return (lookup.all || []).find((pc) => pc.url && pc.url.includes(image.id)) || null;
}

export function resolveScanPointCloud(lookup, image) {
  if (!image) return null;
  return findPointCloudBySegment(lookup, image)
    || findPointCloudByEmbeddedId(lookup, image)
    || lookup.byName.get(image.name)
    || null;
}

export function buildProjectChains(projects) {
  const byId = new Map((projects || []).map((p) => [p.id, p]));
  const chains = new Map();
  const chainFor = (project) => {
    if (chains.has(project.id)) return chains.get(project.id);
    const lineage = [];
    const seen = new Set();
    let node = project;
    while (node && !seen.has(node.id)) {
      seen.add(node.id);
      lineage.unshift(node);
      node = node.parentId ? byId.get(node.parentId) : null;
    }
    const nearestYear = [...lineage].reverse().find((n) => n.year);
    const chain = {
      ids: Object.freeze(lineage.map((n) => n.id)),
      names: Object.freeze(lineage.map((n) => n.name || '')),
      year: nearestYear ? String(nearestYear.year) : null,
      archived: lineage.some((n) => !!n.isArchived),
    };
    chains.set(project.id, chain);
    return chain;
  };
  (projects || []).forEach(chainFor);
  return chains;
}

export function buildScanIndex({ projects, levels, pointClouds }) {
  const safeLevels = Array.isArray(levels) ? levels : [];
  const pcLookup = buildPointCloudLookup(pointClouds);
  const chains = buildProjectChains(projects);
  const entries = [];
  let maxDepth = 0;

  for (const project of projects || []) {
    const chain = chains.get(project.id);
    for (const image of project.images || []) {
      maxDepth = Math.max(maxDepth, chain.names.length);
      entries.push({
        id: image.id,
        name: image.name || '',
        nameLower: String(image.name || '').toLowerCase(),
        image,
        projectId: project.id,
        pathIds: chain.ids,
        pathNames: chain.names,
        year: chain.year,
        archived: chain.archived,
        levelId: findLevelIdForPosition(safeLevels, image.forgeCameraPosition),
        pointCloud: resolveScanPointCloud(pcLookup, image),
      });
    }
  }

  // PointCloudModelInfo.ProjectId is set on every point cloud, so a per-scan PC can carry its
  // leaf project's id too. Only a project PC that is no scan's own PC counts as "compiled".
  // Rebuilt from the full list so a scan PC listed first cannot hide the project's compiled PC.
  const scanPcIds = new Set(entries.filter((e) => e.pointCloud).map((e) => e.pointCloud.id));
  const compiledByProjectId = new Map();
  for (const pc of pcLookup.all) {
    if (pc.projectId && !scanPcIds.has(pc.id) && !compiledByProjectId.has(pc.projectId)) {
      compiledByProjectId.set(pc.projectId, pc);
    }
  }
  pcLookup.byProjectId = compiledByProjectId;

  return {
    entries,
    maxDepth,
    levels: safeLevels.map((l) => ({ id: l.id, name: l.name, bottomElevation: l.bottomElevation })),
    pcLookup,
  };
}

/**
 * Freezes the index, its entries array, every entry and the levels so Vue 2 skips observing them
 * (observe() ignores non-extensible objects). A panel reading the index then tracks ONE dependency
 * (state.scanIndex) instead of every scan field. Shallow per entry on purpose: entry.image stays the
 * live reactive scan, because 360-bubble visibility and point-cloud state are written through it.
 * The pcLookup Maps are left alone: Vue 2 does not observe Maps. Returns the same (now frozen) index.
 */
export function deepFreezeIndex(index) {
  index.entries.forEach((entry) => Object.freeze(entry));
  index.levels.forEach((level) => Object.freeze(level));
  Object.freeze(index.entries);
  Object.freeze(index.levels);
  return Object.freeze(index);
}

/** The index before any facility data has loaded. */
export const EMPTY_SCAN_INDEX = deepFreezeIndex(buildScanIndex({ projects: [], levels: [], pointClouds: [] }));
